/* eslint-disable react/no-unescaped-entities */
"use client";

import React, { useState } from "react";

import EnqueryForm from "@/components/forms/EnqueryForm";
import { FormHeading, SuccessMessageType } from "@/interface/types";

const DownArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M12.854 9.35375L8.35403 13.8538C8.30759 13.9002 8.25245 13.9371 8.19175 13.9623C8.13105 13.9874 8.06599 14.0004 8.00028 14.0004C7.93457 14.0004 7.86951 13.9874 7.80881 13.9623C7.74811 13.9371 7.69296 13.9002 7.64653 13.8538L3.14653 9.35375C3.05271 9.25993 3 9.13268 3 9C3 8.86732 3.05271 8.74007 3.14653 8.64625C3.24035 8.55243 3.3676 8.49972 3.50028 8.49972C3.63296 8.49972 3.76021 8.55243 3.85403 8.64625L7.50028 12.2931V2.5C7.50028 2.36739 7.55296 2.24021 7.64672 2.14645C7.74049 2.05268 7.86767 2 8.00028 2C8.13289 2 8.26006 2.05268 8.35383 2.14645C8.4476 2.24021 8.50028 2.36739 8.50028 2.5V12.2931L12.1465 8.64625C12.2403 8.55243 12.3676 8.49972 12.5003 8.49972C12.633 8.49972 12.7602 8.55243 12.854 8.64625C12.9478 8.74007 13.0006 8.86732 13.0006 9C13.0006 9.13268 12.9478 9.25993 12.854 9.35375Z"
      fill="#3399FF"
    />
  </svg>
);

type FaqAnswer =
  | string
  | { intro?: string; list: string[] }
  | React.ReactElement;
type FaqItem = { value: string; question: string; answer: FaqAnswer };

// EA (Enrolled Agent) FAQs
const faqsData: FaqItem[] = [
  {
    value: "ea-1",
    question: "What is the EA course?",
    answer:
      "The EA (Enrolled Agent) is the highest taxation credential awarded directly by the IRS (Internal Revenue Service) in the United States. It is one of the world’s most respected qualifications for professionals specializing in U.S. taxation.",
  },
  {
    value: "ea-2",
    question: "What is the full form of EA?",
    answer: "EA stands for Enrolled Agent.",
  },
  {
    value: "ea-3",
    question: "Is EA valid in India?",
    answer:
      "Yes. EA professionals are highly valued by Big 4 firms, MNCs, Global Capability Centers (GCCs), and U.S. taxation firms operating in India.",
  },
  {
    value: "ea-4",
    question: "Is EA good for U.S. taxation careers?",
    answer:
      "Yes. EA is one of the best qualifications for professionals interested in U.S. taxation, tax compliance, tax advisory, and IRS representation.",
  },
  {
    value: "ea-5",
    question: "Is EA better than CA or CPA?",
    answer: {
      intro: "Each qualification serves different career goals:",
      list: [
        "CA is focused on Indian accounting and taxation",
        "CPA focuses on accounting, audit, and finance",
        "EA focuses specifically on U.S. taxation and IRS representation",
      ],
    },
  },
  {
    value: "ea-6",
    question: "How many exams are there in EA?",
    answer: {
      intro: "The EA course has 3 exam parts:",
      list: [
        "Part 1 – Individuals",
        "Part 2 – Businesses",
        "Part 3 – Representation, Practice & Procedures",
      ],
    },
  },
  {
    value: "ea-7",
    question: "What is the duration of the EA course?",
    answer: "Most students complete the EA course within 12–15 months.",
  },
  {
    value: "ea-8",
    question: "Can working professionals pursue EA?",
    answer:
      "Yes. The EA course is specifically designed for working professionals.",
  },
  {
    value: "ea-9",
    question: "Are EA exams conducted in India?",
    answer:
      "Yes. EA exams can be taken at Prometric test centers in India or from home through Prometric's online remote proctoring option, subject to eligibility requirements.",
  },
  {
    value: "ea-10",
    question: "Is there negative marking in EA exams?",
    answer: "No. There is no negative marking.",
  },
  {
    value: "ea-11",
    question: "Who is eligible for the EA exam?",
    answer: {
      intro: "One of the biggest advantages of EA is that there is:",
      list: [
        "No mandatory accounting degree requirement",
        "No articleship requirement",
        "No mandatory work experience requirement to appear for the exam",
      ],
    },
  },
  {
    value: "ea-12",
    question: "Can I do EA after B.Com?",
    answer: "Yes. B.Com students and graduates can pursue EA.",
  },
  {
    value: "ea-13",
    question: "Can non-commerce students pursue EA?",
    answer:
      "Yes. Candidates from non-accounting and non-commerce backgrounds can also pursue EA.",
  },
  {
    value: "ea-14",
    question: "Can final-year students apply for EA?",
    answer:
      "Yes. Students can start preparing for EA while pursuing graduation.",
  },
  {
    value: "ea-15",
    question: "Can engineers pursue EA?",
    answer:
      "Yes. Engineers and professionals from other backgrounds interested in taxation can pursue EA.",
  },
  {
    value: "ea-16",
    question: "Is a bachelor’s degree required for EA?",
    answer:
      "No. A bachelor’s degree is not mandatory to appear for the EA exam.",
  },
  {
    value: "ea-17",
    question: "Is articleship required for EA?",
    answer: "No. EA does not require articleship.",
  },
  {
    value: "ea-18",
    question: "Can I pursue EA while working?",
    answer:
      "Yes. The EA course structure is highly flexible for working professionals.",
  },
  {
    value: "ea-19",
    question: "Can I pursue EA alongside another qualification?",
    answer:
      "Yes. Many students pursue EA alongside CPA, CMA, CA, or graduation.",
  },
  {
    value: "ea-20",
    question: "What subjects are covered in EA?",
    answer: {
      intro: "The EA syllabus includes:",
      list: [
        "Individual taxation",
        "Business taxation",
        "IRS representation",
        "Tax procedures",
        "Ethics and compliance",
      ],
    },
  },
  {
    value: "ea-21",
    question: "What is covered in EA Part 1?",
    answer: {
      intro: "EA Part 1 focuses on:",
      list: [
        "Individual taxation",
        "Income and assets",
        "Deductions and credits",
        "Taxpayer advisory",
        "Specialized returns for individuals",
      ],
    },
  },
  {
    value: "ea-22",
    question: "What is covered in EA Part 2?",
    answer: {
      intro: "EA Part 2 focuses on:",
      list: [
        "Business taxation",
        "Business entities",
        "Corporate tax returns",
        "Business tax compliance",
      ],
    },
  },
  {
    value: "ea-23",
    question: "What is covered in EA Part 3?",
    answer: {
      intro: "EA Part 3 covers:",
      list: [
        "IRS representation",
        "Tax procedures",
        "Filing processes",
        "Representation rights",
        "Ethics and compliance",
      ],
    },
  },
  {
    value: "ea-24",
    question: "Are EA exams MCQ-based?",
    answer: "Yes. EA exams are 100% Multiple Choice Questions (MCQs).",
  },
  {
    value: "ea-25",
    question: "Are there simulations or essays in EA exams?",
    answer: "No. EA exams do not include simulations or essays.",
  },
  {
    value: "ea-26",
    question: "How many hours should I study for EA?",
    answer: "Most candidates study around 10–12 hours per week.",
  },
  {
    value: "ea-27",
    question: "How long do I get to clear all EA exams?",
    answer:
      "Once you pass your first EA exam, you get 2 years to clear the remaining two parts.",
  },
  {
    value: "ea-28",
    question: "What is CAIRA?",
    answer:
      "CAIRA stands for Certified AI-Ready Accountant. It is Miles Education’s AI-integrated credential designed for accountants, auditors, tax professionals, and finance teams.",
  },
  {
    value: "ea-29",
    question: "Why is CAIRA integrated with EA?",
    answer:
      "Modern taxation firms increasingly use AI workflows, automation systems, analytics, and intelligent tax operations. CAIRA helps students become AI-ready tax professionals.",
  },
  {
    value: "ea-30",
    question: "Will AI replace tax professionals?",
    answer:
      "AI will automate repetitive tax work, but professionals who understand AI workflows and AI-assisted taxation systems will remain highly valuable.",
  },
  {
    value: "ea-31",
    question: "What AI tools are taught in CAIRA?",
    answer: {
      intro: "Students learn tools such as:",
      list: [
        "Microsoft Copilot",
        "Power BI",
        "Power Automate",
        "Copilot Studio",
        "AI workflow systems",
      ],
    },
  },
  {
    value: "ea-32",
    question: "What are AI workflows in taxation?",
    answer: {
      intro: "AI workflows automate repetitive tax processes such as:",
      list: [
        "Tax drafting",
        "Compliance tracking",
        "Notice response drafting",
        "Tax analytics",
        "Documentation workflows",
        "Tax research automation",
      ],
    },
  },
  {
    value: "ea-33",
    question: "What is an AI-Ready EA?",
    answer:
      "An AI-Ready EA combines taxation expertise with AI execution capability across tax operations, compliance, analytics, and advisory workflows.",
  },
  {
    value: "ea-34",
    question: "Why are firms hiring AI-ready tax professionals?",
    answer:
      "Big 4 firms and MNCs are increasingly adopting AI-powered tax systems and need professionals who can operate and manage AI-enabled tax workflows.",
  },
  {
    value: "ea-35",
    question: "Is CAIRA included with Miles EA?",
    answer: "Yes. CAIRA is integrated into the Miles EA learning ecosystem.",
  },
  {
    value: "ea-36",
    question: "What are examples of AI use cases taught in EA + CAIRA?",
    answer: {
      intro: "Students are exposed to:",
      list: [
        "1040 Orchestration Agents",
        "Corporate Return Agents",
        "Tax Notice Response Copilots",
        "International Tax Planning Agents",
        "Penalty & Abatement Copilots",
        "State Tax Apportionment Workflows",
      ],
    },
  },
  {
    value: "ea-37",
    question: "Can AI automate taxation workflows?",
    answer: {
      intro: "Yes. AI can automate repetitive tax tasks such as:",
      list: [
        "Tax preparation",
        "Compliance workflows",
        "Document intake",
        "Tax notice drafting",
        "Tax analytics",
        "Reporting workflows",
      ],
    },
  },
  {
    value: "ea-38",
    question: "How is AI used in taxation today?",
    answer: {
      intro: "AI is increasingly used for:",
      list: [
        "Tax return drafting",
        "Tax research",
        "Compliance automation",
        "Multi-state taxation",
        "IRS notice responses",
        "Advisory support workflows",
      ],
    },
  },
  {
    value: "ea-39",
    question: "Why is AI important for tax professionals today?",
    answer:
      "Taxation is moving from manual execution to AI-assisted, exception-driven workflows. Firms now value professionals who can combine tax expertise with automation and AI systems.",
  },
  {
    value: "ea-40",
    question: "What jobs can I get after EA?",
    answer: {
      intro: "Common roles include:",
      list: [
        "U.S. Tax Associate",
        "Tax Compliance Analyst",
        "IRS Representation Specialist",
        "Tax Reviewer",
        "International Tax Consultant",
        "Corporate Tax Associate",
      ],
    },
  },
  {
    value: "ea-41",
    question: "What is the salary after EA?",
    answer:
      "EA professionals are increasingly in demand across U.S. taxation practices, with strong salary growth opportunities depending on role and experience.",
  },
  {
    value: "ea-42",
    question: "Are EAs in demand globally?",
    answer:
      "Yes. There is a growing global demand for professionals specializing in U.S. taxation and compliance.",
  },
  {
    value: "ea-43",
    question: "Can I work in U.S. taxation after EA?",
    answer:
      "Yes. EA is specifically designed for careers in U.S. taxation and IRS representation.",
  },
  {
    value: "ea-44",
    question: "Do Big 4 firms hire EAs?",
    answer:
      "Yes. Big 4 firms actively hire EA professionals for U.S. taxation and global tax compliance roles.",
  },
  {
    value: "ea-45",
    question: "Why choose Miles Education for EA?",
    answer: {
      intro: "Miles combines:",
      list: [
        "U.S. taxation learning",
        "AI-readiness through CAIRA",
        "Career support",
        "Industry-focused mentorship",
      ],
    },
  },
  {
    value: "ea-46",
    question: "Does Miles provide placement support?",
    answer:
      "Yes. Miles provides placement support with Big 4 firms and 220+ MNCs.",
  },
  {
    value: "ea-47",
    question: "Does Miles offer online EA classes?",
    answer: {
      intro: "Yes. Miles offers:",
      list: [
        "Live online classes",
        "Recorded learning support",
        "Mentorship",
        "Practice systems",
        "Learning assistance",
      ],
    },
  },
  {
    value: "ea-48",
    question: "What learning resources are provided by Miles?",
    answer: {
      intro: "Students receive:",
      list: [
        "100+ instructional lectures",
        "3,000+ study questions",
        "Unlimited practice exams",
        "Digital flashcards",
        "Integrated IRS publications",
      ],
    },
  },
  {
    value: "ea-49",
    question: "Does Miles offer mentorship support?",
    answer: {
      intro: "Yes. Students receive:",
      list: [
        "One-on-one mentoring",
        "Career guidance",
        "Strategy support",
        "Academic assistance",
      ],
    },
  },
  {
    value: "ea-50",
    question: "Does Miles support AI learning for tax professionals?",
    answer:
      "Yes. CAIRA is fully integrated into the Miles EA journey and focuses on AI workflows, automation systems, analytics, and AI agents for taxation professionals.",
  },
];

const cpaData: FaqItem[] = [
  {
    value: "ea-tab-1",
    question: "What is the EA course?",
    answer: (
      <div className="about-section-content">
        <p>
          The <strong>EA (Enrolled Agent)</strong> is the highest taxation
          credential awarded directly by the{" "}
          <strong>IRS (Internal Revenue Service)</strong>, the U.S.
          government&rsquo;s tax authority. It is one of the world&rsquo;s most
          respected qualifications for professionals who want to build careers
          in{" "}
          <strong>
            U.S. taxation, tax advisory, tax compliance, and IRS representation
          </strong>
          .
        </p>
        <p>
          An <strong>Enrolled Agent</strong> is federally authorized to
          represent taxpayers before the IRS for:
        </p>
        <ul className="about-section-list">
          <li>Tax filings</li>
          <li>Audits</li>
          <li>Appeals</li>
          <li>Compliance matters</li>
          <li>Tax notices</li>
          <li>Tax disputes</li>
          <li>Advisory services</li>
        </ul>
        <p>
          In simple terms, if the <strong>CPA</strong> is known as the gold
          standard for accounting and auditing, the <strong>EA</strong> is
          considered one of the highest-value credentials in{" "}
          <strong>U.S. taxation</strong>.
        </p>
        <p>
          Today,{" "}
          <strong>
            Big 4 firms, multinational companies, Global Capability Centers
            (GCCs), accounting firms, and tax advisory firms
          </strong>{" "}
          across the world are actively hiring professionals with U.S. taxation
          expertise. As global taxation becomes increasingly complex, the demand
          for professionals who understand both tax regulations and modern tax
          technology is growing rapidly.
        </p>
        <p>But taxation itself is changing.</p>
        <p>
          <strong>Yesterday&rsquo;s tax professional:</strong>
        </p>
        <ul className="about-section-list">
          <li>Manually entered data</li>
          <li>Checked forms</li>
          <li>Reconciled 1099s</li>
          <li>Tracked deadlines manually</li>
          <li>Drafted notices manually</li>
        </ul>
        <p>
          <strong>Tomorrow&rsquo;s EA:</strong>
        </p>
        <ul className="about-section-list">
          <li>Builds AI-powered tax workflows</li>
          <li>Uses AI agents for tax research</li>
          <li>Automates tax compliance processes</li>
          <li>Leverages intelligent dashboards</li>
          <li>Handles strategic tax advisory</li>
          <li>Works with AI-assisted tax operations</li>
        </ul>
        <p>The new hiring question at modern firms is:</p>
        <p>
          <em>
            &ldquo;Can you help us automate tax compliance and implement AI
            across tax workflows?&rdquo;
          </em>
        </p>
        <p>
          That is exactly why <strong>Miles Education</strong> has integrated
          the EA program with{" "}
          <strong>CAIRA (Certified AI-Ready Accountant)</strong>, a structured
          AI credential designed specifically for accountants, tax
          professionals, and finance teams.
        </p>
        <p>
          The combination of <strong>EA + CAIRA</strong> helps students become
          not just tax professionals, but{" "}
          <strong>AI-ready tax specialists</strong> prepared for the future of
          taxation.
        </p>
      </div>
    ),
  },
  {
    value: "ea-tab-2",
    question: "What Does EA Stand For?",
    answer: (
      <div className="about-section-content">
        <p>
          <strong>EA stands for Enrolled Agent.</strong>
        </p>
        <p>
          It is a <strong>federally authorized U.S. taxation credential</strong>{" "}
          issued directly by the <strong>IRS</strong>. Unlike many accounting
          certifications that are issued by private associations, the EA
          credential comes from the <strong>U.S. government itself</strong>.
        </p>
        <p>
          An EA has{" "}
          <strong>unlimited representation rights before the IRS</strong>,
          meaning they can represent:
        </p>
        <ul className="about-section-list">
          <li>Individuals</li>
          <li>Businesses</li>
          <li>Partnerships</li>
          <li>Corporations</li>
          <li>Trusts</li>
          <li>Estates</li>
        </ul>
        <p>
          This makes EA one of the most{" "}
          <strong>specialized and respected qualifications</strong> in U.S.
          taxation.
        </p>
      </div>
    ),
  },
  {
    value: "ea-tab-3",
    question: "What Does an Enrolled Agent Do?",
    answer: (
      <div className="about-section-content">
        <p>
          An EA works primarily in{" "}
          <strong>taxation and tax advisory functions</strong>.
        </p>
        <p>Their responsibilities include:</p>
        <ul className="about-section-list">
          <li>Preparing U.S. tax returns</li>
          <li>Handling individual taxation</li>
          <li>Managing business taxation</li>
          <li>Responding to IRS notices</li>
          <li>Representing taxpayers before the IRS</li>
          <li>Supporting tax planning</li>
          <li>Managing tax compliance workflows</li>
          <li>Advising on international taxation</li>
          <li>Supporting HNI taxation and wealth planning</li>
        </ul>
        <p>
          Because EAs specialize in <strong>U.S. federal taxation</strong>, they
          are highly valued by:
        </p>
        <ul className="about-section-list">
          <li>
            <strong>Big 4 firms</strong>
          </li>
          <li>U.S. accounting firms</li>
          <li>Global taxation teams</li>
          <li>MNC tax departments</li>
          <li>Tax consulting firms</li>
        </ul>
      </div>
    ),
  },
  {
    value: "ea-tab-4",
    question: "Why is the EA Course Becoming Popular?",
    answer: (
      <div className="about-section-content">
        <p>
          The demand for <strong>U.S. taxation professionals</strong> has
          increased significantly because of:
        </p>
        <ul className="about-section-list">
          <li>Rapid growth of U.S. accounting outsourcing</li>
          <li>Expansion of Global Capability Centers (GCCs)</li>
          <li>Increasing complexity in international taxation</li>
          <li>Growing shortage of tax professionals in the U.S.</li>
          <li>Adoption of AI and automation in tax workflows</li>
        </ul>
        <p>
          The EA course is becoming especially popular among students and
          working professionals because:
        </p>
        <ul className="about-section-list">
          <li>
            It has only <strong>3 exam parts</strong>
          </li>
          <li>
            Exams are <strong>fully MCQ-based</strong>
          </li>
          <li>
            <strong>No mandatory accounting degree</strong> is required
          </li>
          <li>It is designed for working professionals</li>
          <li>It focuses directly on U.S. taxation</li>
          <li>
            It can be completed within <strong>12&ndash;15 months</strong>
          </li>
        </ul>
        <p>
          Compared to many traditional accounting qualifications, EA offers one
          of the <strong>fastest pathways</strong> into global taxation careers.
        </p>
      </div>
    ),
  },
  {
    value: "ea-tab-5",
    question: "EA Exam Structure",
    answer: (
      <div className="about-section-content">
        <p>
          The EA course consists of <strong>3 exam parts</strong>:
        </p>
        <p>
          <strong>EA Part 1 &ndash; Individuals</strong>
        </p>
        <p>Covers:</p>
        <ul className="about-section-list">
          <li>Taxpayer data</li>
          <li>Individual taxation</li>
          <li>Income and assets</li>
          <li>Deductions and credits</li>
          <li>Individual tax advisory</li>
          <li>Specialized returns for individuals</li>
        </ul>
        <p>
          <strong>EA Part 2 &ndash; Businesses</strong>
        </p>
        <p>Covers:</p>
        <ul className="about-section-list">
          <li>Business taxation</li>
          <li>Business entities</li>
          <li>Corporate tax preparation</li>
          <li>Specialized tax returns</li>
          <li>Tax compliance for businesses</li>
        </ul>
        <p>
          <strong>
            EA Part 3 &ndash; Representation, Practice &amp; Procedures
          </strong>
        </p>
        <p>Covers:</p>
        <ul className="about-section-list">
          <li>IRS representation</li>
          <li>Tax procedures</li>
          <li>Filing processes</li>
          <li>Taxpayer representation</li>
          <li>Ethics and compliance</li>
        </ul>
        <p>
          One of the biggest advantages of EA is its{" "}
          <strong>flexibility</strong>:
        </p>
        <ul className="about-section-list">
          <li>
            Exams are available from <strong>May to February</strong>
          </li>
          <li>You choose your exam date</li>
          <li>
            Once you pass the first exam, you get <strong>2 years</strong> to
            clear the remaining parts
          </li>
          <li>You can study one part at a time</li>
        </ul>
        <p>The exams are:</p>
        <ul className="about-section-list">
          <li>
            <strong>100% MCQ-based</strong>
          </li>
          <li>Computer-based</li>
          <li>Designed for working professionals</li>
        </ul>
      </div>
    ),
  },
  {
    value: "ea-tab-6",
    question: "Who Can Pursue EA?",
    answer: (
      <div className="about-section-content">
        <p>
          One of the biggest advantages of the EA course is{" "}
          <strong>accessibility</strong>.
        </p>
        <p>
          Unlike many accounting qualifications, EA{" "}
          <strong>does not require</strong>:
        </p>
        <ul className="about-section-list">
          <li>A mandatory accounting degree</li>
          <li>Articleship</li>
          <li>Prior taxation experience</li>
        </ul>
        <p>The EA course is suitable for:</p>
        <ul className="about-section-list">
          <li>B.Com graduates</li>
          <li>BBA graduates</li>
          <li>Working professionals</li>
          <li>Tax professionals</li>
          <li>Commerce students</li>
          <li>CA/CPA/CMA aspirants</li>
          <li>Professionals interested in U.S. taxation</li>
          <li>Candidates looking for global tax careers</li>
        </ul>
        <p>
          Even candidates from <strong>non-accounting backgrounds</strong> can
          pursue EA successfully.
        </p>
      </div>
    ),
  },
  {
    value: "ea-tab-7",
    question: "What is CAIRA and Why is it Integrated with EA?",
    answer: (
      <div className="about-section-content">
        <p>Taxation today is increasingly driven by:</p>
        <ul className="about-section-list">
          <li>AI-powered workflows</li>
          <li>Automation systems</li>
          <li>Tax research copilots</li>
          <li>Intelligent dashboards</li>
          <li>AI-assisted compliance systems</li>
        </ul>
        <p>
          Modern tax professionals are expected to understand{" "}
          <strong>both taxation and AI-enabled execution</strong>.
        </p>
        <p>
          <strong>CAIRA (Certified AI-Ready Accountant)</strong> is Miles
          Education&rsquo;s AI-integrated accounting and finance credential
          built specifically for accountants and tax professionals.
        </p>
        <p>
          Through <strong>CAIRA</strong>, EA students learn:
        </p>
        <ul className="about-section-list">
          <li>
            <strong>Microsoft Copilot</strong>
          </li>
          <li>
            <strong>Power BI</strong>
          </li>
          <li>
            <strong>Power Automate</strong>
          </li>
          <li>
            <strong>Copilot Studio</strong>
          </li>
          <li>AI workflow systems</li>
          <li>AI agents for accounting and taxation</li>
        </ul>
        <p>
          Students are exposed to <strong>real-world AI use cases</strong> such
          as:
        </p>
        <ul className="about-section-list">
          <li>1040 Orchestration Agents</li>
          <li>Corporate Return Agents</li>
          <li>Tax Notice Response Copilots</li>
          <li>State Tax Apportionment Workflows</li>
          <li>International Tax Planning Agents</li>
          <li>Wealth Transfer Planning Assistants</li>
          <li>Penalty &amp; Abatement Copilots</li>
        </ul>
        <p>The idea is simple:</p>
        <p>
          <strong>EA gives you tax authority.</strong>
          <br />
          <strong>CAIRA gives you the AI edge.</strong>
        </p>
        <p>
          Together, they help students become{" "}
          <strong>future-ready tax professionals</strong>.
        </p>
      </div>
    ),
  },
  {
    value: "ea-tab-8",
    question: "Career Opportunities After EA",
    answer: (
      <div className="about-section-content">
        <p>The EA credential opens pathways across:</p>
        <ul className="about-section-list">
          <li>
            <strong>Big 4 firms</strong>
          </li>
          <li>U.S. taxation firms</li>
          <li>Global Capability Centers (GCCs)</li>
          <li>Corporate taxation teams</li>
          <li>International tax consulting</li>
          <li>Public accounting firms</li>
          <li>HNI taxation and advisory</li>
        </ul>
        <p>Popular job roles include:</p>
        <ul className="about-section-list">
          <li>U.S. Tax Associate</li>
          <li>IRS Representation Specialist</li>
          <li>Tax Compliance Analyst</li>
          <li>International Tax Consultant</li>
          <li>Corporate Tax Associate</li>
          <li>HNI Tax Advisor</li>
          <li>Tax Operations Specialist</li>
          <li>Tax Reviewer</li>
        </ul>
        <p>
          Because of the <strong>global shortage of tax professionals</strong>{" "}
          and the increasing complexity of U.S. taxation, EA professionals are
          <strong> increasingly in demand worldwide</strong>.
        </p>
      </div>
    ),
  },
  {
    value: "ea-tab-9",
    question: "Why Choose Miles Education for EA?",
    answer: (
      <div className="about-section-content">
        <p>
          <strong>Miles Education</strong> combines:
        </p>
        <ul className="about-section-list">
          <li>U.S. taxation expertise</li>
          <li>AI-readiness training</li>
          <li>Career support</li>
          <li>Industry-focused mentorship</li>
        </ul>
        <p>Students receive:</p>
        <ul className="about-section-list">
          <li>Live online classes</li>
          <li>AI-integrated taxation workflows</li>
          <li>CAIRA training</li>
          <li>1:1 mentorship</li>
          <li>Placement support</li>
          <li>Career guidance</li>
          <li>Practical taxation exposure</li>
        </ul>
        <p>
          The Miles <strong>learning ecosystem</strong> includes:
        </p>
        <ul className="about-section-list">
          <li>
            <strong>100+ instructional lectures</strong>
          </li>
          <li>
            <strong>3,000+ study questions</strong>
          </li>
          <li>Unlimited practice exams</li>
          <li>Integrated IRS publications</li>
          <li>Mentorship support</li>
          <li>AI masterclasses</li>
        </ul>
        <p>
          Miles also works with <strong>Big 4 firms and 220+ MNCs</strong>{" "}
          hiring AI-ready accountants and tax professionals.
        </p>
        <p>
          For students and professionals looking to build a global taxation
          career while staying relevant in the{" "}
          <strong>AI-driven future of finance</strong>, the{" "}
          <strong>EA + CAIRA pathway</strong> offers a modern and future-focused
          approach to taxation education.
        </p>
      </div>
    ),
  },
];

// Miles data
const milesData: FaqItem[] = [
  {
    value: "miles-1",
    question: "About Miles Education",
    answer:
      "The global talent landscape has critical gaps, but at Miles Education, we're not just filling these voids; we're building pathways to success. Driven by our mission to work backward from industry shortages, skill gaps, and opportunities, we empower businesses and enable talent by combining academic learning with hands-on experience through our unique ED-WORK Pedagogy.",
  },
];

const NewFaqSection = ({ pageName }: { pageName: string }) => {
  const [activeTab, setActiveTab] = useState("FAQs");
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpand = (value: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case "FAQs":
        return faqsData;
      case "EA":
        return cpaData;
      case "Miles":
        return milesData;
      default:
        return faqsData;
    }
  };

  const visibleItems = showAllFaqs
    ? getCurrentData()
    : getCurrentData().slice(0, 3);

  const TAB_GRADIENT =
    "radial-gradient(1474.29% 161.47% at 113.4% 210.95%, #b9dcff 0%, #3a9cff 100%)";

  // Tailwind arbitrary variants for rich-text styles inside accordion answers
  const accordionRichText =
    // .answer
    "[&_.answer]:text-sm [&_.answer]:font-normal [&_.answer]:leading-6 " +
    // .faqList
    "[&_.faqList]:list-disc [&_.faqList]:ml-5 [&_.faqList]:text-sm [&_.faqList]:font-normal [&_.faqList]:my-1 " +
    // .faqTableWrapper
    "[&_.faqTableWrapper]:overflow-x-auto [&_.faqTableWrapper]:mt-3 [&_.faqTableWrapper]:mb-2 [&_.faqTableWrapper]:border [&_.faqTableWrapper]:border-[#E5EAF2] " +
    // .faqTable
    "[&_.faqTable]:w-full [&_.faqTable]:border-collapse [&_.faqTable]:text-[0.8rem] [&_.faqTable]:bg-white " +
    // .faqTable thead
    "[&_.faqTable_thead]:bg-[#EAF2FC] " +
    // .faqTable th & td
    "[&_.faqTable_th]:px-4 [&_.faqTable_th]:py-3 [&_.faqTable_th]:text-left [&_.faqTable_th]:text-[#1d1d1d] [&_.faqTable_th]:font-semibold [&_.faqTable_th]:align-top " +
    "[&_.faqTable_td]:px-4 [&_.faqTable_td]:py-3 [&_.faqTable_td]:text-left [&_.faqTable_td]:text-[#1d1d1d] [&_.faqTable_td]:align-top " +
    // zebra striping
    "[&_.faqTable_tbody_tr:nth-child(even)]:bg-[#F7FBFF] " +
    // first column muted label
    "[&_.faqTable_tbody_tr_td:first-child]:!text-[#94A3B8] [&_.faqTable_tbody_tr_td:first-child]:font-medium " +
    // .about-section-content
    "[&_.about-section-content]:text-sm [&_.about-section-content]:leading-[1.55] [&_.about-section-content]:text-[#999999] " +
    "[&_.about-section-content_p]:mt-0 [&_.about-section-content_p]:mb-3 " +
    // .about-section-list
    "[&_.about-section-list]:list-disc [&_.about-section-list]:mt-2 [&_.about-section-list]:mb-3 [&_.about-section-list]:ml-5 [&_.about-section-list]:text-sm " +
    "[&_.about-section-list_li]:mb-[0.35rem]";

  return (
    <section className="py-8 md:pt-12 md:pb-16 md:bg-[#FFFF]">
      <div className="main_container w-full px-4 md:max-w-[1280px] md:mx-auto md:px-6">
        <div className="flex flex-col gap-6 min-[1200px]:flex-row min-[1200px]:gap-8 min-[1200px]:items-stretch">
          {/* Form column — order 1 mobile, order 2 desktop */}
          <div
            id="live-webinar-section"
            className="w-full order-1 md:order-2 md:w-[380px] md:flex-[0_0_380px]"
          >
            <div
              id="live-webinar-form"
              className="rounded-2xl bg-white border border-[#DEE7F5] p-5"
              style={{
                transition: "all 0.5s ease-in-out",
                position: "sticky",
                top: 100,
              }}
            >
              <EnqueryForm
                pageName={pageName}
                courseId={
                  pageName === "CPA"
                    ? 1
                    : pageName === "CPA/CMA"
                      ? 1
                      : pageName === "CMA"
                        ? 2
                        : pageName === "EA"
                          ? 19
                          : 1
                }
                darkBackground={false}
                submitButtonName="Request a Callback"
                formType="Enquiry Form"
                isAStepperForm
                successMessage={SuccessMessageType.ACCOUNTING}
                formHeading={FormHeading.TALK_TO_AN_EXPERT}
              />
            </div>
          </div>

          {/* Content column — order 2 mobile, order 1 desktop */}
          <div className="w-full order-2 md:order-1 md:w-[833px] md:flex-[0_0_833px]">
            <div className="bg-white rounded-2xl py-6 px-4">
              <div className="flex flex-col justify-center md:flex-row md:gap-3 md:items-center md:justify-center">
                <h2 className="text-2xl font-bold text-center mb-4 text-[#333] md:text-[2rem] md:mb-0">
                  All About
                </h2>

                <div className="flex p-1 items-center rounded-2xl bg-[#f3f3f3] mb-6 w-full md:w-[337px] md:mb-0 md:flex-none">
                  <button
                    className={`flex p-2 justify-center items-center gap-2.5 flex-1 rounded-2xl border-0 text-base font-semibold cursor-pointer transition-all duration-300 md:text-lg ${
                      activeTab === "EA"
                        ? "text-white"
                        : "bg-transparent text-[#1d1d1d]"
                    }`}
                    style={
                      activeTab === "EA"
                        ? { background: TAB_GRADIENT }
                        : undefined
                    }
                    onClick={() => setActiveTab("EA")}
                  >
                    EA
                  </button>
                  <button
                    className={`flex p-2 justify-center items-center gap-2.5 flex-1 rounded-2xl border-0 text-base font-semibold cursor-pointer transition-all duration-300 md:text-lg ${
                      activeTab === "Miles"
                        ? "text-white"
                        : "bg-transparent text-[#1d1d1d]"
                    }`}
                    style={
                      activeTab === "Miles"
                        ? { background: TAB_GRADIENT }
                        : undefined
                    }
                    onClick={() => setActiveTab("Miles")}
                  >
                    Miles
                  </button>
                  <button
                    className={`flex p-2 justify-center items-center gap-2.5 flex-1 rounded-2xl border-0 text-base font-semibold cursor-pointer transition-all duration-300 md:text-lg ${
                      activeTab === "FAQs"
                        ? "text-white"
                        : "bg-transparent text-[#1d1d1d]"
                    }`}
                    style={
                      activeTab === "FAQs"
                        ? { background: TAB_GRADIENT }
                        : undefined
                    }
                    onClick={() => setActiveTab("FAQs")}
                  >
                    FAQs
                  </button>
                </div>
              </div>

              <div className="mt-4">
                {visibleItems.map((item) => {
                  const enableReadMore = activeTab === "EA";
                  const isExpanded = expandedItems.has(item.value);
                  const isCollapsed = enableReadMore && !isExpanded;
                  return (
                    <div
                      key={item.value}
                      className={`flex flex-col gap-2.5 p-4 rounded-2xl bg-[#F7FBFF] mb-4 border-0 ${accordionRichText}`}
                    >
                      <div className="text-sm font-bold text-[#333] p-0 w-full text-left leading-normal md:text-base">
                        {item.question}
                      </div>
                      <div
                        className={`relative p-0 text-xs font-normal leading-normal text-[#999] md:text-sm ${
                          isCollapsed ? "max-h-[120px] overflow-hidden" : ""
                        }`}
                      >
                        {typeof item.answer === "string"
                          ? item.answer
                          : React.isValidElement(item.answer)
                            ? item.answer
                            : (() => {
                                const ans = item.answer as {
                                  intro?: string;
                                  list: string[];
                                };
                                return (
                                  <>
                                    {ans.intro && (
                                      <p className="answer">{ans.intro}</p>
                                    )}
                                    <ul className="faqList">
                                      {ans.list.map((li, idx) => (
                                        <li key={idx}>{li}</li>
                                      ))}
                                    </ul>
                                  </>
                                );
                              })()}
                        {isCollapsed && (
                          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#F7FBFF] to-transparent" />
                        )}
                      </div>
                      {enableReadMore && (
                        <button
                          type="button"
                          onClick={() => toggleExpand(item.value)}
                          className="self-start mt-1 inline-flex items-center bg-transparent text-[#39f] border-0 text-xs md:text-sm font-bold cursor-pointer p-0"
                        >
                          {isExpanded ? "Read Less" : "Read More"}
                        </button>
                      )}
                    </div>
                  );
                })}

                {getCurrentData().length > 3 && (
                  <button
                    className="flex items-center justify-center gap-1 mt-4 mx-auto p-0 bg-transparent text-[#39f] border-0 text-sm font-bold cursor-pointer w-full"
                    onClick={() => setShowAllFaqs(!showAllFaqs)}
                  >
                    {showAllFaqs ? "See Less" : "See More"}
                    <span
                      className="inline-flex transition-transform duration-200"
                      style={{
                        transform: showAllFaqs ? "rotate(180deg)" : "none",
                      }}
                    >
                      <DownArrowIcon />
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewFaqSection;
